import React from 'react';
import { COLOR } from '@src/styles/color';
import { useTranslation } from 'react-i18next';
import Typography from '@src/new-components/Typography';
import pluralize from '@src/utils/pluralize';
import {
  calculateMinutesFromWords,
  calculateWordsFromMinutes,
  formatDuration,
  calculateA4Pages,
} from '@src/services/package';
import classNames from 'classnames';
import {
  CONTENT_MAX,
  CONTENT_MIN,
  WORD_MAX,
  WORD_MIN,
  WORD_STEP,
} from '@src/constants/package';
import { StyledNeedForm, StyledNeedsFormWrap } from './index.style';
import SliderData from './SliderData';
import GroupOption from './GroupOption';

const NeedForm = ({ valueSlider, setValueSlider, options, setOptions }) => {
  const { t, i18n } = useTranslation();
  const { language } = i18n;

  const onChangeSlider = (value, name) => {
    const newValue = Number(value.target.value);

    if (name === 'yourScriptWordCount') {
      const newMinutes = calculateMinutesFromWords(newValue);

      setValueSlider({
        ...valueSlider,
        yourScriptWordCount: newValue,
        yourContentDuration: newMinutes,
      });
    } else if (name === 'yourContentDuration') {
      const newWordCount = calculateWordsFromMinutes(newValue);
      const roundedWordCount = Math.round(newWordCount / WORD_STEP) * WORD_STEP;
      const clampedWordCount = Math.max(
        WORD_MIN,
        Math.min(WORD_MAX, roundedWordCount),
      );
      const adjustedMinutes = calculateMinutesFromWords(clampedWordCount);

      setValueSlider({
        ...valueSlider,
        yourContentDuration: adjustedMinutes,
        yourScriptWordCount: clampedWordCount,
      });
    } else {
      setValueSlider({
        ...valueSlider,
        [name]: newValue,
      });
    }
  };

  const onChangeOption = (optionName, checked) => {
    setOptions({
      ...options,
      [optionName]: checked,
    });
  };

  const contentData = [
    {
      label: t('numberOfContentsProduced'),
      unit: 'contents',
      name: 'numberOfContentsProduced',
      min: CONTENT_MIN,
      max: CONTENT_MAX,
      step: 1,
    },
    {
      label: t('yourContentDuration'),
      name: 'yourContentDuration',
      min: calculateMinutesFromWords(WORD_MIN),
      max: calculateMinutesFromWords(WORD_MAX),
      step: 1,
    },
    {
      label: t('yourScriptWordCount'),
      unit: 'word',
      name: 'yourScriptWordCount',
      min: WORD_MIN,
      max: WORD_MAX,
      step: WORD_STEP,
    },
  ];

  return (
    <StyledNeedsFormWrap>
      <div className="need-form-title">
        <Typography variant="body-16-semibold" color={COLOR.darkGray}>
          {t('averageMonthlyNeed')}
        </Typography>
      </div>
      <div className="need-form-content-wrap">
        {contentData.map((item) => (
          <StyledNeedForm key={item.name}>
            <div className="need-form-content">
              <SliderData
                marksData={item.marks}
                valueSlider={valueSlider[item.name]}
                onChangeSlider={(value) => onChangeSlider(value, item.name)}
                labelMetric={item.label}
                minNumber={item.min}
                maxNumber={item.max}
                step={item.step}
              />
            </div>
            <div className="need-form-data">
              <Typography
                className={classNames('need-form-data-text', {
                  'need-form-data-text-time': [
                    'yourContentDuration',
                    'numberOfContentsProduced',
                  ].includes(item.name),
                  'need-form-data-text-word':
                    item.name === 'yourScriptWordCount',
                })}
                variant="heading-32-bold"
                color={COLOR.darkGray}
              >
                {item.name === 'yourContentDuration'
                  ? formatDuration(
                      valueSlider[item.name],
                    ).value?.toLocaleString(language)
                  : valueSlider[item.name]?.toLocaleString(language)}
              </Typography>
              <Typography variant="body-14-regular" color={COLOR.darkGray}>
                {item.name === 'yourContentDuration'
                  ? `${pluralize(
                      formatDuration(valueSlider[item.name]),
                      t(formatDuration(valueSlider[item.name]).unit),
                      language,
                    )}`
                  : t(item.unit)}
              </Typography>
              {item.name === 'yourScriptWordCount' && (
                <Typography
                  variant="body-16-regular"
                  color={COLOR.descriptionUpload}
                  className="more-info-inside"
                  dangerouslySetInnerHTML={{
                    __html: t('approxOnePageA4', {
                      pages: calculateA4Pages(
                        valueSlider[item.name],
                      )?.toLocaleString(language),
                    }),
                  }}
                />
              )}
            </div>
          </StyledNeedForm>
        ))}
        <Typography
          variant="body-16-regular"
          color={COLOR.descriptionUpload}
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            marginTop: '-28px',
          }}
          className="more-info-outside"
          dangerouslySetInnerHTML={{
            __html: t('approxOnePageA4', {
              pages: calculateA4Pages(
                valueSlider.yourScriptWordCount,
              )?.toLocaleString(language),
            }),
          }}
        />
      </div>

      <GroupOption
        useForeignVoice={options.useForeignVoice}
        useVoiceClone={options.useVoiceClone}
        onChangeOption={onChangeOption}
      />
    </StyledNeedsFormWrap>
  );
};

export default NeedForm;
